/** @public */
export type PushSdkCustomClassNames = {
    webPushUnsupported?: string
    webPushSupported?: string
    subscriptionNone?: string
    subscriptionSubscribed?: string
    subscriptionUnsubscribed?: string
    subscriptionDenied?: string
    subscriptionDismissed?: string
    promptIneligible?: string
    promptEligible?: string
}

/**
 * The visitor's consent to push notifications and tracking, as the site's consent platform reports
 * it. `pending` means the visitor has not answered yet, which is not the same as `denied`.
 *
 * @public
 */
export type PushSdkConsentState = 'granted' | 'denied' | 'pending'

/** @public */
export type PushSdkConfig = {
    domainKey: string
    sw?: string
    swScope?: string
    externalId?: string
    classNames?: PushSdkCustomClassNames

    /**
     * Set to `true` to stop the `pushly_udr` URL parameter from requesting deletion. Any page
     * running the SDK otherwise acts on it, including through a link shared or crafted by someone
     * else. `request_user_deletion` stays available from code either way.
     */
    disableUserDeletionUrlTrigger?: boolean

    /**
     * The visitor's consent to push notifications and tracking, as your consent platform reports it.
     * Pass it on every page, and report changes with `pushly('consent', state)`.
     *
     * - `'pending'`: the visitor has not answered yet. The SDK waits: it starts nothing, sends
     *   nothing and leaves an existing subscription alone, until `granted` or `denied` is reported.
     * - `'denied'`: before the SDK starts anything, a subscribed browser is unsubscribed and reported
     *   as opted out, and nothing else is sent. A browser with nothing to withdraw sends nothing.
     * - `'granted'`: the SDK loads, and an opt-out caused by an earlier `denied` is undone. An opt-out
     *   the visitor asked for through `opt_out`, and a deletion, are not.
     * - Absent: the SDK behaves as it did before this setting existed.
     */
    consent?: PushSdkConsentState

    /** @deprecated Has no effect. Use `consent`. */
    consentRequired?: boolean
    /** @deprecated Has no effect. Use `consent`. */
    requireConsent?: boolean
    /** @deprecated */
    frameMessenger?: string
}

// The `satisfies` below makes a missing or unknown key a compile error.
const pushSdkConfigKeys: readonly (keyof PushSdkConfig)[] = Object.keys({
    domainKey: true,
    sw: true,
    swScope: true,
    externalId: true,
    classNames: true,
    disableUserDeletionUrlTrigger: true,
    consent: true,

    // silenced known deprecations
    consentRequired: true,
    requireConsent: true,
    frameMessenger: true,
} satisfies Record<keyof PushSdkConfig, true>) as (keyof PushSdkConfig)[]

/** @internal */
export function pushSdkConfigContainsUnknownKeys(config: PushSdkConfig): boolean {
    return Object.keys(config).some((key) => !pushSdkConfigKeys.includes(key as keyof PushSdkConfig))
}

const consentStates: readonly PushSdkConsentState[] = ['granted', 'denied', 'pending']

/**
 * The consent state the SDK acts on, or `undefined` for anything else. Every way consent reaches the
 * SDK goes through here, so this is the one place a richer form, such as one state per purpose,
 * would be accepted. Booleans are refused: `false` is the natural way to pass a banner nobody has
 * answered yet, and that must not withdraw a subscriber.
 *
 * @internal
 */
export function normalizeConsentState(value: unknown): PushSdkConsentState | undefined {
    return consentStates.includes(value as PushSdkConsentState) ? (value as PushSdkConsentState) : undefined
}
